import { createSlice } from "@reduxjs/toolkit";
import {
    createExamination,
    getExamination,
    getExaminationById,
    startExamination,
    pauseExamination,
    completeExamination,
    getExaminationQuestions,
    submitExaminationAnswer,
    getExaminationTimeStatus,
    createExaminationAndNavigate,
    clearExaminationError,
    loadQuestionById
} from "../actions/examinationActionsHelper";
import { examinationsObject } from "../stateObject/examinationsObject";

const initialState = examinationsObject;

const examinationSlice = createSlice({
    name: "examination",
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => {
            return initialState;
        },
        setCurrentQuestion: (state, action) => {
            state.currentQuestion = action.payload;
        },
        setCurrentQuestionIndex: (state, action) => {
            state.currentQuestionIndex = action.payload;
        },
        setUserAnswer: (state, action) => {
            const { questionIndex, answer } = action.payload;
            state.userAnswers[questionIndex] = answer;
        },
        cacheQuestion: (state, action) => {
            const { questionIndex, question } = action.payload;
            state.questionCache[questionIndex] = question;
        },
        // New lightweight navigation actions
        navigateToQuestion: (state, action) => {
            const { sectionIndex, questionIndex } = action.payload;
            state.currentSectionIndex = sectionIndex;
            state.currentQuestionIndexInSection = questionIndex;
        },
        navigateToSection: (state, action) => {
            state.currentSectionIndex = action.payload;
            state.currentQuestionIndexInSection = 0;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get examinations
            .addCase(getExamination.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getExamination.success, (state, action) => {
                state.examinations = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getExamination.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to fetch examinations.";
            })

            // Create examination
            .addCase(createExamination.request, (state) => {
                console.log('🔄 Reducer: createExamination.request');
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createExamination.success, (state, action) => {
                console.log('✅ Reducer: createExamination.success with payload:', action.payload);

                // Extract examination data from nested response structure
                const examinationData = action.payload?.data?.examination || action.payload?.examination || action.payload;
                console.log('📝 Extracted examination data:', examinationData);

                state.currentExamination = examinationData;
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createExamination.failed, (state, action) => {
                console.log('❌ Reducer: createExamination.failed with error:', action.payload);
                state.isCreating = false;
                state.error = action.payload || "Failed to create examination.";
            })

            // Get examination by ID
            .addCase(getExaminationById.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getExaminationById.success, (state, action) => {
                console.log('✅ Reducer: getExaminationById.success with payload:', action.payload);

                // Extract examination data from nested response structure
                const examinationData = action.payload?.data?.examination || action.payload?.examination || action.payload;
                console.log('📝 Extracted examination data from getById:', examinationData);

                state.currentExamination = examinationData;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(getExaminationById.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to fetch examination.";
            })

            // Start examination
            .addCase(startExamination.request, (state) => {
                state.isStarting = true;
                state.error = null;
            })
            .addCase(startExamination.success, (state, action) => {
                if (state.currentExamination) {
                    state.currentExamination.status = 'in-progress';
                }
                state.isStarting = false;
                state.error = null;
            })
            .addCase(startExamination.failed, (state, action) => {
                state.isStarting = false;
                state.error = action.payload || "Failed to start examination.";
            })

            // Get examination questions
            .addCase(getExaminationQuestions.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getExaminationQuestions.success, (state, action) => {
                const { currentQuestion, preloadedQuestions, sectionInfo, examination } = action.payload;
                state.currentQuestion = currentQuestion;
                state.sectionInfo = sectionInfo;

                // Update examination data if provided
                if (examination) {
                    state.currentExamination = examination;
                }

                // Cache current and preloaded questions
                if (currentQuestion) {
                    state.questionCache[state.currentQuestionIndex] = currentQuestion;
                }

                if (preloadedQuestions && Array.isArray(preloadedQuestions)) {
                    preloadedQuestions.forEach((question, idx) => {
                        state.questionCache[state.currentQuestionIndex + idx + 1] = question;
                    });
                }

                state.isLoading = false;
                state.error = null;
            })
            .addCase(getExaminationQuestions.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load questions.";
            })

            // Submit examination answer
            .addCase(submitExaminationAnswer.request, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitExaminationAnswer.success, (state, action) => {
                state.isSubmitting = false;
                state.error = null;
                // Update user answer in current question if provided
                if (action.payload && action.payload.userAnswer) {
                    const { questionId, answer } = action.payload.userAnswer;
                    if (state.currentQuestion && state.currentQuestion._id === questionId) {
                        if (!state.currentQuestion.userAnswer) {
                            state.currentQuestion.userAnswer = {};
                        }
                        state.currentQuestion.userAnswer.answer = answer;
                    }
                }
            })
            .addCase(submitExaminationAnswer.failed, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to submit answer.";
            })

            // Get time status
            .addCase(getExaminationTimeStatus.request, (state) => {
                // Don't show loading for time status requests (they're frequent)
                state.error = null;
            })
            .addCase(getExaminationTimeStatus.success, (state, action) => {
                state.timeStatus = action.payload;
                state.error = null;
            })
            .addCase(getExaminationTimeStatus.failed, (state, action) => {
                // Don't show error for time status failures (they're frequent)
                console.warn('Time status fetch failed:', action.payload);
            })

            // Complete examination
            .addCase(completeExamination.request, (state) => {
                state.isCompleting = true;
                state.error = null;
            })
            .addCase(completeExamination.success, (state, action) => {
                if (state.currentExamination) {
                    state.currentExamination.status = 'completed';
                }
                state.isCompleting = false;
                state.error = null;
            })
            .addCase(completeExamination.failed, (state, action) => {
                state.isCompleting = false;
                state.error = action.payload || "Failed to complete examination.";
            })

            // Pause examination
            .addCase(pauseExamination.request, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(pauseExamination.success, (state, action) => {
                if (state.currentExamination) {
                    state.currentExamination.status = 'paused';
                }
                state.isLoading = false;
                state.error = null;
            })
            .addCase(pauseExamination.failed, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to pause examination.";
            })

            // Create examination and navigate
            .addCase(createExaminationAndNavigate.request, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createExaminationAndNavigate.success, (state, action) => {
                state.currentExamination = action.payload;
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createExaminationAndNavigate.failed, (state, action) => {
                state.isCreating = false;
                state.error = action.payload || "Failed to create examination.";
            })

            // Load individual question (lightweight approach)
            .addCase(loadQuestionById.request, (state) => {
                state.isLoadingQuestion = true;
                state.questionError = null;
            })
            .addCase(loadQuestionById.success, (state, action) => {
                const { questionId, questionData } = action.payload;
                state.questionCache[questionId] = questionData;
                state.currentQuestion = questionData;
                state.isLoadingQuestion = false;
                state.questionError = null;
            })
            .addCase(loadQuestionById.failed, (state, action) => {
                state.isLoadingQuestion = false;
                state.questionError = action.payload || 'Failed to load question.';
            })

            // Clear error
            .addCase(clearExaminationError, (state) => {
                state.error = null;
                state.questionError = null;
            });
    },
});

export const {
    setLoading,
    clearError,
    resetState: resetExaminationState,
    setCurrentQuestion,
    setCurrentQuestionIndex,
    setUserAnswer,
    cacheQuestion,
    navigateToQuestion: navigateToQuestionAction,
    navigateToSection: navigateToSectionAction
} = examinationSlice.actions;

export { navigateToQuestionAction as navigateToQuestion, navigateToSectionAction as navigateToSection };
export default examinationSlice.reducer;